import React, { useEffect, useRef } from 'react';
import '../Pages/styles.css'; // Import the prefixed CSS as requested

// --- Import FontAwesomeIcon Component ---
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// --- Import ALL Necessary Icons from Font Awesome (Updated for Commissioning Assistance) ---
import {
    // Core Concepts & General
    faUsers,
    faCog,              // Commissioning / Setup / Operations
    faCheckCircle,      // Operational Excellence / Success / Compliance / Readiness
    faHospital,         // Healthcare Facility
    faUserTie,          // Consulting / Experts / HR Professionals
    faClipboardList,    // Planning / Checklists / SOPs
    faLink,             // Alignment / Integration
    faUsersCog,         // Manpower / HR Structuring / Team / Utilization
    faShieldAlt,        // Risk Reduction / Safety / Security
    faFileAlt,          // Policies / Manuals / Documentation
    faChartLine,        // ROI / Performance Tracking
    // Scope of Services Icons
    // faUsersCog reused for HR
    faLaptopMedical,    // IT & Information Systems
    faTasks,            // Operations / Workflow Optimization
    faBullhorn,         // Marketing Strategy / Branding
    faBuilding,         // Infrastructure & Systems Readiness
    faBalanceScale,     // Regulatory / Accreditation / Compliance (Legal aspect)
    faAward,            // Accreditation / Standards (Quality aspect)
    faRocket,           // Launch Assistance / Go-Live
    // Why Choose Us Icons
    // faUserTie reused for Expertise
    faMedal,            // Proven Experience
    // faCheckCircle / faBalanceScale reused for Compliance
    // faCog reused for Customized Planning
    faLaptopCode,       // Technology-Integrated
    faLifeRing,         // Post-Launch Support
    // Keep other potentially useful icons if needed
    // Icons likely unused from previous versions:
    /*
     faSitemap, faLightbulb, faMapMarkedAlt, faProjectDiagram, faTools, faSearchLocation,
     faBoxOpen, faComments, faTags, faStar, faPrint, faMousePointer, faNewspaper,
     faCalendarCheck, faCrosshairs, faNetworkWired, faTrophy, faThumbsUp, faDollarSign
    */
} from '@fortawesome/free-solid-svg-icons'; // Using free solid icons


// --- Intersection Observer Hooks (No changes needed in hook logic) ---
const useIntersectionObserverTitle = (options) => {
    const containerRef = useRef(null);
    const [isVisible, setIsVisible] = React.useState(false);
    const callbackFunction = (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) setIsVisible(true);
    };
    useEffect(() => {
        const observer = new IntersectionObserver(callbackFunction, options);
        const currentRef = containerRef.current;
        if (currentRef) observer.observe(currentRef);
        return () => { if (currentRef) observer.unobserve(currentRef); };
    }, [containerRef, options]);
    return [containerRef, isVisible];
};

const useIntersectionObserverElements = (options) => {
    useEffect(() => {
        const observerCallback = (entries, observer) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    const target = entry.target;
                    const parent = target.parentElement;
                    const siblings = parent ? Array.from(parent.children).filter(el => el.classList.contains('cliniqor-fade-in-up')) : [target];
                    const index = siblings.indexOf(target);
                    const staggerIndex = index >= 0 ? index : 0;

                    target.style.setProperty('--item-index', staggerIndex % 8); // Keep stagger logic
                    target.classList.add('cliniqor-visible'); // Use prefixed class
                    observer.unobserve(target);
                }
            });
        };
        const observer = new IntersectionObserver(observerCallback, options);
        const targets = document.querySelectorAll('.cliniqor-fade-in-up');
        targets.forEach(target => observer.observe(target));
        return () => targets.forEach(target => { if (target) observer.unobserve(target); });
    }, [options]);
};
// --- End Observer Hooks ---


function App() {

    // --- Observer Setup for Titles (Adjusted Refs) ---
    const titleObserverOptions = { threshold: 0.2 };
    const [title1Ref, title1Visible] = useIntersectionObserverTitle(titleObserverOptions); // Why Crucial?
    const [title2Ref, title2Visible] = useIntersectionObserverTitle(titleObserverOptions); // Cliniqor's Approach / Team
    const [title3Ref, title3Visible] = useIntersectionObserverTitle(titleObserverOptions); // Scope of Services (7 points)
    const [title4Ref, title4Visible] = useIntersectionObserverTitle(titleObserverOptions); // Why Choose Cliniqor?

    // --- Observer Setup for General Fade-In Elements ---
    useIntersectionObserverElements({ threshold: 0.1 });

    // Choose a relevant hero background image for commissioning/setup/operations
    const heroBgImage = 'https://images.unsplash.com/photo-1542744095-fcf48d80b0fd?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1920&q=80'; // Image showing planning/teamwork

  return(
    // Use prefixed base wrapper class
    <div className="cliniqor-app-wrapper">

      {/* --- HERO SECTION --- */}
      <header
        className="cliniqor-hero-section" // Prefixed
        style={{ backgroundImage: `url(${heroBgImage})` }}
      >
        <div className="cliniqor-container"> {/* Prefixed */}
          <div className="cliniqor-hero-content"> {/* Prefixed */}
            {/* --- NEW HERO CONTENT --- */}
            <h1>Commissioning Assistance Services</h1>
            <p className="cliniqor-sub-heading">
              Setting the Foundation for Operational Excellence with Cliniqor – Hospital & Healthcare Consulting Experts
            </p>
            <p className="cliniqor-intro-text">
              Beyond construction, operational readiness is key. Cliniqor offers comprehensive Commissioning Assistance to ensure your facility is commissioned for long-term success, efficiency, and quality care.
            </p>
            {/* --- END NEW HERO CONTENT --- */}
          </div>
        </div>
      </header>

      <main>
        {/* --- WHY COMMISSIONING IS CRUCIAL --- */}
        <section className="cliniqor-section cliniqor-section-first cliniqor-features-section cliniqor-section-bg-gradient"> {/* Use features layout */}
          <div className="cliniqor-container">
            <div ref={title1Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title1Visible ? 'cliniqor-visible' : ''}`}>
              <h2>Why Commissioning Assistance is Crucial</h2>
              <p>A necessity for alignment, optimization, risk reduction, and ROI in today's healthcare.</p>
            </div>
             {/* Using Features Grid for the benefits */}
            <div className="cliniqor-features-grid">
                <div className="cliniqor-feature-card cliniqor-fade-in-up">
                    <div className="cliniqor-feature-card-content">
                        <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faLink} /></div>
                        <h4>Seamless Alignment</h4>
                        <p>Ensuring clinical infrastructure matches patient care delivery needs.</p>
                    </div>
                </div>
                <div className="cliniqor-feature-card cliniqor-fade-in-up">
                    <div className="cliniqor-feature-card-content">
                        <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faUsersCog} /></div> {/* Utilization */}
                        <h4>Optimal Utilization</h4>
                        <p>Maximizing use of space, manpower, and technology resources.</p>
                    </div>
                </div>
                <div className="cliniqor-feature-card cliniqor-fade-in-up">
                    <div className="cliniqor-feature-card-content">
                        <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faShieldAlt} /></div>
                        <h4>Reduced Risks</h4>
                        <p>Minimizing challenges and potential issues during the critical startup phase.</p>
                    </div>
                </div>
                <div className="cliniqor-feature-card cliniqor-fade-in-up">
                    <div className="cliniqor-feature-card-content">
                        <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faUsers} /></div> {/* Onboarding */}
                        <h4>Structured Onboarding</h4>
                        <p>Effectively integrating medical and non-medical teams into operations.</p>
                    </div>
                </div>
                 <div className="cliniqor-feature-card cliniqor-fade-in-up">
                    <div className="cliniqor-feature-card-content">
                        <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faFileAlt} /></div>
                        <h4>Strong Foundations</h4>
                        <p>Establishing robust policies, SOPs, and process frameworks from the start.</p>
                    </div>
                </div>
                 <div className="cliniqor-feature-card cliniqor-fade-in-up">
                    <div className="cliniqor-feature-card-content">
                        <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faChartLine} /></div>
                        <h4>Enhanced ROI</h4>
                        <p>Boosting return on investment through efficiency and meticulous planning.</p>
                    </div>
                </div>
            </div>
            <div className="cliniqor-text-content cliniqor-fade-in-up" style={{ textAlign: 'center', marginTop: '20px' }}>
                 <p>The success of a project lies in its operational readiness and execution. Cliniqor plays a vital role here.</p>
            </div>
          </div>
        </section>

        {/* --- CLINIQOR'S APPROACH & TEAM --- */}
        <section className="cliniqor-section cliniqor-section-bg-light">
          <div className="cliniqor-container">
            <div ref={title2Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title2Visible ? 'cliniqor-visible' : ''}`}>
              <h2>Our Approach to Commissioning Assistance at Cliniqor</h2>
              <p>A phased, process-oriented methodology ensuring 360° readiness.</p>
            </div>
             <div className="cliniqor-text-content cliniqor-fade-in-up">
                 <p>We ensure each component—manpower, technology, protocols, patient services—is thoughtfully evaluated and integrated. Our team of hospital planners, facility managers, experts, clinical advisors, HR professionals, IT specialists, and marketing consultants provides a holistic solution for new and upcoming hospitals.</p>
             </div>
          </div>
        </section>


        {/* --- SCOPE OF SERVICES (Detailed Planning Section - 7 items) --- */}
        <section className="cliniqor-section cliniqor-section-bg-texture cliniqor-detailed-planning-section"> {/* Kept existing classes */}
          <div className="cliniqor-container">
             <div className="cliniqor-section-title cliniqor-fade-in-up"> {/* No specific ref needed here if title3 is below */}
              <h2>Scope of Cliniqor’s Commissioning Services</h2>
               <p>Comprehensive support across all critical operational domains.</p>
            </div>
            {/* Using Planning Items for the 7 Service Areas */}
            <div className="cliniqor-planning-items-wrapper">
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faUsersCog} /></div>
                    <div className="cliniqor-content"><h4>1. Human Resource Structuring & Management</h4> <p>Org structure, manpower planning, compensation models, JDs/KRAs, Employee Manual, Training & Induction (soft skills, clinical, communication, etiquette).</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faLaptopMedical} /></div>
                    <div className="cliniqor-content"><h4>2. IT & Information Systems Planning</h4> <p>HIS selection/setup, module definition, system integration (LMS, PACS, BMS, etc.), hardware/software support, data security, app/portal integration, E-ICU setup.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faTasks} /></div>
                    <div className="cliniqor-content"><h4>3. Operations & Departmental Workflow Optimization</h4> <p>Workflow design, SOP creation , emergency/infection control protocols, tariff setting, checklists, admission/discharge protocols, quality indicators, operational manuals.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faBullhorn} /></div>
                    <div className="cliniqor-content"><h4>4. Marketing Strategy & Brand Identity</h4> <p>Calendar planning, segmentation, physician engagement, internal marketing, digital setup (website, social), branding materials, signage, media strategy.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faBuilding} /></div>
                    <div className="cliniqor-content"><h4>5. Infrastructure & Systems Readiness</h4> <p>Utilities planning, medical equipment commissioning, IT/non-IT integration, vendor coordination, procurement support (linen, furniture), mock drills.</p> </div>
                 </div>
                  <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faAward} /></div> {/* Accreditation */}
                    <div className="cliniqor-content"><h4>6. Regulatory and Accreditation Support</h4> <p>JCI prep, clinical audits, compliance checklists, infection control/BMW protocols, licensing assistance.</p> </div>
                 </div>
                  <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faRocket} /></div>
                    <div className="cliniqor-content"><h4>7. Launch Assistance & Post-Commissioning Support</h4> <p>Launch planning, 30/60/90 day checklists, operational audits, troubleshooting, financial review, continuous improvement plans.</p> </div>
                 </div>
            </div>
          </div>
        </section>

        {/* --- WHY CHOOSE CLINIQOR (Features Section - Reused) --- */}
        <section className="cliniqor-section cliniqor-features-section cliniqor-section-bg-light">
           <div className="cliniqor-container">
              <div ref={title4Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title4Visible ? 'cliniqor-visible' : ''}`}>
                 <h2>Why Choose Cliniqor for Commissioning Assistance?</h2>
                 <p>Ensuring all aspects of your facility launch are expertly managed.</p>
              </div>
              <div className="cliniqor-features-grid">
                    {/* --- Cards for Why Choose --- */}
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faUserTie} /></div>
                            <h4>Multi-disciplinary Expertise</h4>
                            <p>Consultants from clinical, managerial, and technical backgrounds.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faMedal} /></div>
                            <h4>Proven Experience</h4>
                            <p>Successfully commissioned diverse healthcare facilities across regions.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faCheckCircle} /></div>
                            <h4>Compliance-Focused</h4>
                            <p>SOPs and protocols aligned with latest JCI standards.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faCog} /></div> {/* Customized */}
                            <h4>Customized Planning</h4>
                            <p>Tailored commissioning process based on project size, scope, specialty.</p>
                        </div>
                    </div>
                     <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faLaptopCode} /></div>
                            <h4>Technology-Integrated</h4>
                            <p>Bridging traditional systems and new-age tech (HIS to E-ICU).</p>
                        </div>
                    </div>
                     <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faLifeRing} /></div>
                            <h4>Post-Launch Support</h4>
                            <p>Commitment continues with audits, feedback, and training after opening.</p>
                        </div>
                    </div>
               </div>
           </div>
        </section>

      </main>

      {/* --- FOOTER CTA --- */}
      <footer className="cliniqor-section cliniqor-section-narrow cliniqor-footer-cta">
        <div className="cliniqor-container cliniqor-fade-in-up">
          {/* --- UPDATED FOOTER CONTENT --- */}
          <h2>Lay the Groundwork for Operational Excellence</h2>
          <p>Commissioning determines if your venture thrives. With Cliniqor, you don't just commission a hospital—you establish a future-ready, patient-centric, and operationally sound institution. Let Cliniqor turn your vision into a sustainable, successful reality.</p>
          <a href="#contact" className="cliniqor-cta-button">Request Commissioning Assistance</a>
          {/* --- END UPDATED FOOTER CONTENT --- */}
        </div>
      </footer>
    </div> // End cliniqor-app-wrapper
  );
}

export default App;